import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { ShieldCheck, Loader2, Copy, Check, QrCode, LogOut } from "lucide-react";

export default function MFASetup() {
  const navigate = useNavigate();
  const { token, user, updateUser, logout } = useAuth();
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // If MFA already enabled, redirect to dashboard
    if (user?.mfa_enabled) {
      navigate("/dashboard");
      return;
    }

    const fetchSetupData = async () => {
      try {
        const response = await axios.get(`${API}/auth/mfa/setup`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSetupData(response.data);
      } catch (error) {
        console.error("MFA setup error:", error);
        toast.error("Failed to initialize MFA setup");
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchSetupData();
    }
  }, [token, user, navigate]);

  const handleCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(setupData.secret);
      setCopied(true);
      toast.success("Secret key copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy secret key");
    }
  };

  const handleEnableMFA = async () => {
    if (code.length !== 6) {
      toast.error("Please enter the complete 6-digit code");
      return;
    }

    setVerifying(true);
    try {
      await axios.post(
        `${API}/auth/mfa/enable`,
        { temp_token: "", code },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      updateUser({ ...user, mfa_enabled: true });
      toast.success("MFA enabled successfully! Welcome to STPmembership.");
      navigate("/dashboard");
    } catch (error) {
      console.error("MFA enable error:", error);
      toast.error(error.response?.data?.detail || "Invalid code. Please try again.");
      setCode("");
    } finally {
      setVerifying(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" data-testid="mfa-setup-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
              <QrCode className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-gray-900 text-lg">STPmembership</h1>
              <p className="text-xs text-gray-500">Security Setup Required</p>
            </div>
          </div>
          <Button variant="ghost" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="mr-2 h-4 w-4" />
            Sign Out
          </Button>
        </div>

        {/* Alert Banner */}
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800">
            <strong>Security Requirement:</strong> Two-factor authentication must be enabled before you can access the application.
          </p>
        </div>

        <Card className="border-0 shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mb-4">
              <ShieldCheck className="w-8 h-8 text-blue-600" />
            </div>
            <CardTitle className="text-2xl">Set up Two-Factor Authentication</CardTitle>
            <CardDescription>
              Welcome, {user?.name}! Please set up MFA to secure your account.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8 pt-6">
            {/* Step 1: Scan QR Code */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-semibold">
                  1
                </div>
                <h3 className="text-lg font-semibold">Scan QR Code</h3>
              </div>
              <p className="text-sm text-gray-600 ml-11">
                Open your authenticator app (Google Authenticator, Authy, Microsoft Authenticator, etc.) and scan this QR code:
              </p>
              <div className="flex justify-center py-4">
                <div className="p-4 bg-white rounded-xl border-2 border-gray-100 shadow-sm">
                  {setupData?.qr_code && (
                    <img 
                      src={setupData.qr_code} 
                      alt="MFA QR Code" 
                      className="w-48 h-48"
                      data-testid="mfa-qr-code"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Step 2: Manual Entry */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center text-sm font-semibold">
                  2
                </div>
                <h3 className="text-lg font-semibold">Or enter manually</h3>
              </div>
              <p className="text-sm text-gray-600 ml-11">
                If you can't scan the QR code, enter this secret key in your authenticator app:
              </p>
              <div className="ml-11 flex items-center gap-2">
                <code className="flex-1 px-4 py-3 bg-gray-100 rounded-lg font-mono text-sm break-all" data-testid="mfa-secret">
                  {setupData?.secret}
                </code>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopySecret}
                  data-testid="copy-secret-btn"
                >
                  {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {/* Step 3: Verify */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center text-sm font-semibold">
                  3
                </div>
                <h3 className="text-lg font-semibold">Verify setup</h3>
              </div>
              <p className="text-sm text-gray-600 ml-11">
                Enter the 6-digit code from your authenticator app to complete setup:
              </p>
              <div className="ml-11 space-y-4">
                <InputOTP
                  value={code}
                  onChange={setCode}
                  maxLength={6}
                  disabled={verifying}
                  data-testid="mfa-verify-input"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>

                <Button 
                  onClick={handleEnableMFA}
                  className="w-full h-11 btn-press" 
                  disabled={verifying || code.length !== 6}
                  data-testid="enable-mfa-btn"
                >
                  {verifying ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    "Enable MFA & Continue"
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-gray-500 mt-6">
          Need help? Contact your system administrator.
        </p>
      </div>
    </div>
  );
}
